import { useEffect, useState } from "react";
import { Link } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { customFetch, useListProjects, useCreateProject, getListProjectsQueryKey, type Project, type ProjectListItem } from "@workspace/api-client-react";
import { emitTourEvent } from "@/hooks/use-guided-tour";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { useAuth, getTrialInfo } from "@/hooks/use-auth";
import { PRO_FEATURES } from "@workspace/pricing";
import { FolderKanban, Plus, ExternalLink, Lock } from "lucide-react";

interface Props {
  kind: "quote" | "invoice";
  id: number;
}

const NONE_VALUE = "__none__";
const NEW_VALUE = "__new__";

export function ProjectAssignment({ kind, id }: Props) {
  const { user } = useAuth();
  const { hasProAccess: isPro } = getTrialInfo(user);

  if (!isPro) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <FolderKanban style={{ width: 14, height: 14, color: "hsl(var(--muted-foreground))" }} />
        <Label style={{ fontSize: "0.8125rem", margin: 0 }}>Project:</Label>
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Link
              href="/upgrade"
              className="inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-400 dark:hover:bg-amber-900/40"
            >
              <Lock className="w-3 h-3" />
              <span>Assign to project — Upgrade to Pro</span>
            </Link>
          </TooltipTrigger>
          <TooltipContent>Upgrade to Pro to unlock {PRO_FEATURES.PROJECTS}</TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return <ProjectAssignmentContent kind={kind} id={id} />;
}

function ProjectAssignmentContent({ kind, id }: Props) {
  const path = kind === "quote" ? "estimates" : "invoices";
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data: projects } = useListProjects();
  const { data: current, refetch } = useQuery({
    queryKey: [`/api/${path}/${id}/project`],
    queryFn: () => customFetch<{ project: Project | null }>(`/api/${path}/${id}/project`),
  });
  const setProject = useMutation({
    mutationFn: (projectId: number | null) =>
      customFetch(`/api/${path}/${id}/project`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectId }),
      }),
    onSuccess: () => {
      refetch();
      qc.invalidateQueries({ queryKey: getListProjectsQueryKey() });
      toast({ title: "Project updated" });
      if (kind === "quote") emitTourEvent("quote.project-linked");
    },
  });
  const createProject = useCreateProject({
    onSuccess: (project) => {
      qc.invalidateQueries({ queryKey: getListProjectsQueryKey() });
      setProject.mutate(project.id);
      setShowNew(false);
      setNewName("");
    },
  });

  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState("");

  const value = current?.project ? String(current.project.id) : NONE_VALUE;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
      <FolderKanban style={{ width: 14, height: 14, color: "hsl(var(--muted-foreground))" }} />
      <Label style={{ fontSize: "0.8125rem", margin: 0 }}>Project:</Label>
      <Select
        value={value}
        onValueChange={(v) => {
          if (v === NEW_VALUE) { setShowNew(true); return; }
          setProject.mutate(v === NONE_VALUE ? null : Number(v));
        }}
      >
        <SelectTrigger style={{ width: 240 }}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE_VALUE}>None</SelectItem>
          {(projects ?? []).map((p: ProjectListItem) => (
            <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
          ))}
          <SelectItem value={NEW_VALUE}>+ New project…</SelectItem>
        </SelectContent>
      </Select>
      {current?.project && (
        <Link href={`/projects/${current.project.id}`}>
          <Button variant="ghost" size="icon" title="Open project">
            <ExternalLink style={{ width: 14, height: 14 }} />
          </Button>
        </Link>
      )}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent>
          <DialogHeader><DialogTitle>New project</DialogTitle></DialogHeader>
          <div>
            <Label>Name</Label>
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
            <Button disabled={!newName.trim()} onClick={() => createProject.mutate({ name: newName.trim() })}>
              Create &amp; assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
